import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError } from '@/api/client.types';
import { createLegacyKuduToken } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import type {
  EditableQuestion,
  QuestionSectionKind,
  RawComponent,
} from '@/features/content/editor/editorTypes';
import { contentEditorQueryKey } from '@/features/content/editor/useSaveComponent';
import { COURSES_QUERY_KEY } from '@/features/courses/useCourses';
import { courseQueryKey } from '@/features/courses/useCourse';

import { copyQuestions, type CopyQuestionPosition } from './copyQuestionService';

export interface CopyQuestionMutationInput {
  position?: CopyQuestionPosition;
  questions: EditableQuestion[];
  scope: QuestionSectionKind;
  targetComponent: RawComponent;
  targetComponentId: string;
  targetCourseId: string;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

export function useCopyQuestion() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();

  return useMutation<RawComponent, ApiError, CopyQuestionMutationInput>({
    mutationFn: async (params) => {
      if (!user) {
        throw createDataError('You must be signed in to copy questions.');
      }

      if (params.questions.length === 0) {
        throw createDataError('Select at least one question to copy.');
      }

      if (!params.targetCourseId.trim()) {
        throw createDataError('Select a course before copying questions.');
      }

      if (!params.targetComponentId.trim()) {
        throw createDataError('Select a chapter before copying questions.');
      }

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });
      const savedComponent = await copyQuestions(client, {
        courseId: params.targetCourseId,
        position: params.position,
        questions: params.questions,
        scope: params.scope,
        targetComponent: params.targetComponent,
      });
      const targetEditorQueryKey = contentEditorQueryKey(
        params.targetCourseId,
        params.targetComponentId,
      );

      await queryClient.invalidateQueries({ queryKey: targetEditorQueryKey });

      return queryClient.getQueryData<RawComponent>(targetEditorQueryKey) ?? savedComponent;
    },
    onSuccess: async (_savedComponent, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: COURSES_QUERY_KEY }),
        queryClient.invalidateQueries({
          queryKey: courseQueryKey(variables.targetCourseId),
        }),
      ]);
    },
  });
}
